/**
 * Session-start orchestration from the start screen. Routes to
 * `POST /api/games` or `POST /api/games/restore` based on whether the acting player has a run in
 * progress for the gameMode (`GET /api/runs`, via `hooks.hasActiveRun`), adopts the response's
 * `gameId` as the client's explicit game handle (multiplayer-addressable-games design § Client), and
 * falls back through the api.js error path on any create/restore failure. The response's
 * `roundInProgress` picks the tail: a restore that resumed a still-live round renders the board that
 * exists, everything else deals an opening round (`startFirstRound`). A `?seed=` that
 * `buildSessionParams` accepts (numeric — a malformed one is dropped and votes on nothing)
 * overrides the routing onto the create path: only it can honor a seed — on a mode that already
 * had a run, the server now structurally refuses the resulting fork (CorpusWarehouse#store,
 * structural-single-run-enforcement) at the first round-end write, so the seeded game plays on
 * unpersisted rather than silently splitting the career.
 *
 * `game-start-ui.js` owns the active-run presence set and the runs-fetch readiness promise; it
 * threads them in via the `hooks` object, which keeps `startSession` free of DOM and module-state
 * coupling. That holds for `startSession` alone: `playStartScreenExit` queries the mounted
 * `.game-start` itself, and `enterRun` below drives that exit and the render off it.
 */
import {
  api,
  postApi,
  fetchState,
  setCurrentGame,
  getCurrentGame,
  getGame,
  renderFetchedState,
} from "../api.js";
import { setHadApiError, setAnimating } from "../state.js";
import { setSessionConfig } from "../session-config.js";
import { writeLastSelected } from "../player-profile.js";
import { isDevMode } from "../dev-mode.js";
import { getActiveLanguage } from "../i18n.js";
import { armBiddingStageEntrance } from "../animation/bidding-stage-entrance.js";
import { ensureOpen as ensurePushStreamOpen } from "../push-stream.js";
import { track } from "../analytics.js";
import { decodeWireState } from "../serde/decode-wire-state.js";
import { playGameStartExit } from "./game-start-entrance.js";

/**
 * @param settings - { gameMode, hints, playerName }
 * @param hooks.hasActiveRun - (gameMode) => boolean — the acting player has a run in progress
 * @param hooks.runsReady - () => Promise<void> — resolves once the `GET /api/runs` fetch driving
 *   `hasActiveRun` has settled; awaited before routing so a fast Play-click landing before that
 *   fetch resolves can't route on a stale/empty map and fork a duplicate run (2b review gap 2, the
 *   gap-2 fix)
 * @param exitDone - settles when the caller's exit animation has finished. The
 *   create request fires immediately once routing is decided, so its round trip rides under the
 *   exit, but the response is only handled after this settles — the board swap never lands
 *   mid-exit. Callers own rejection handling (pass a non-rejecting promise).
 */
// Split out of startSession for the function-size budget.
function trackGameStarted(settings, endpoint) {
  track("game-started", {
    mode: settings.gameMode.toLowerCase(),
    restored: endpoint === "games/restore",
  });
}

export async function startSession(settings, hooks, exitDone) {
  // writeLastSelected up front covers the case where the player accepts the
  // pre-selected mode without clicking a card. Bypass api() for the create
  // call: SessionReadyDto would crash the animation pipeline; the deal
  // animation comes from the next-round chain.
  writeLastSelected(settings.gameMode);
  try {
    await hooks.runsReady();
  } catch (e) {
    // Run state unknown (the /api/runs fetch failed) — routing either way risks the duplicate-run
    // fork the await exists to prevent, so surface the standard error path instead of guessing.
    await exitDone;
    handleStartSessionError("runs", e?.message ?? e);
    return;
  }
  const params = buildSessionParams(settings);
  const hasRun = hooks.hasActiveRun(settings.gameMode);
  // A seed can only be honored by the create path: RestoreSessionRequest has no seed
  // field and Jackson rejects unknown properties, so a seeded restore is structurally
  // impossible. `== null`, not `!params.seed` — ?seed=0 is a legitimate seed.
  //
  // This deliberately routes onto create even for a mode that already has a run in progress —
  // the very routing the runsReady await above exists to avoid accidentally. Accepted, not
  // overlooked: `?seed=` is a declared dev/test affordance whose whole contract is "a
  // reproducible game from here", which only the create path can honor. It no longer forks a
  // second warehoused row the way it used to: CorpusWarehouse#store (structural-single-run-
  // enforcement) refuses a write for a (player, mode) pair that already holds a different
  // createdAt, so the server throws loudly at the first round-end write and the seeded game
  // plays on unpersisted rather than silently splitting the career.
  const endpoint = hasRun && params.seed == null ? "games/restore" : "games";
  // The client's own request params ARE the session config the server will hold — set it here
  // rather than waiting on a round trip, since the animation controller reads it as early as the
  // next-round response below (autoPlay gates the bidding-ceremony animation).
  setSessionConfig({
    hintsEnabled: params.hints,
    autoPlay: params.autoPlay,
    gameMode: params.gameMode,
  });
  // Plain postApi, not api()/postGame — no gameId exists yet, and SessionReadyDto would crash
  // the animation pipeline; the deal animation comes from the next-round chain below.
  const createResPromise = postApi(endpoint, params);
  // Park rejections until the await below re-surfaces them: without a handler
  // attached now, a network failure mid-exit fires unhandledrejection.
  createResPromise.catch(() => {});
  await exitDone;
  const createRes = await createResPromise;
  if (!createRes.ok) {
    handleStartSessionError(endpoint, createRes.status);
    return;
  }
  trackGameStarted(settings, endpoint);
  const { gameId, roundInProgress } = await createRes.json();
  setCurrentGame(gameId);
  await (roundInProgress ? resumeLiveRound() : startFirstRound());
}

/**
 * Render a round that is already live instead of dealing one — restore's live-first hit, which is
 * the Quit → Play path since no-quit-cheesing. There is no deal to ask for here: `next-round`
 * answers "A round is still in progress" (GameApiController#startRound), and api() would fold that
 * 400 into its generic error recovery — the right board, but hadApiError latched for the rest of
 * the session, which board-layout.js's resize reconciliation and animation-controller.js's
 * eventless mid-game reconcile both gate on.
 *
 * fetchState routes through renderFetchedState, which is also the push-stream opener
 * startFirstRound would otherwise have called. The bidding-stage etch stays unarmed on purpose —
 * it's the board's first introduction, and a resumed board was introduced when its round began
 * (arming it here would leave the one-shot unconsumed, since a resume deals nothing, and replay it
 * on the next round's deal).
 */
function resumeLiveRound() {
  return fetchState();
}

/** Deal the opening round and open the push stream — split from startSession both to keep it under
 *  the function-size budget and because the graph-load recovery is its own concern. */
async function startFirstRound() {
  // Entering the game from the start screen — new game OR Continue. Arm the
  // bidding-stage etch so the first round-start deal inscribes the stage in (the
  // gatehouse + standards' first introduction to the board). Consumed once by that
  // deal; a reload/resume recovery never routes through here, so it never etches.
  armBiddingStageEntrance();
  try {
    await api("next-round", {});
  } catch (e) {
    // The lazy-game-graph-boot-seam introduced a reject source this call never had before: the
    // onAfterApi adapter (boot.js) awaits the game graph's dynamic import (game-graph.js#
    // ensureGameUi), so a failed graph load at this first, cold deal rejects here — where a plain
    // HTTP failure is swallowed by api() into a `false` return. Route it through the same recovery
    // the create/restore failures above use (setHadApiError + fetchState re-fetch), rather than
    // letting it escape as an unhandled rejection past commitAndPlay's un-awaited startSession call.
    // ensureGameUi resets its memo on rejection, so the fetchState re-render retries the import.
    handleStartSessionError("next-round", e?.message ?? e);
    return;
  }
  // The fresh-start stream opener (sse-transport-swap slice 1): api() responses don't route
  // through renderFetchedState (the reload/recovery opener), so a game started here would
  // otherwise carry no push stream until a later reload. Slice 2's application funnel (open
  // on any applied state with a live phase) supersedes this call.
  ensurePushStreamOpen(getCurrentGame());
}

// Split from enterRun for the function-size budget: builds the exitDone promise the same way
// commitAndPlay builds startSession's — every enterRun caller already sits on the start screen,
// so there's always a `.game-start` to play the exit against.
function playStartScreenExit() {
  const screen = document.querySelector(".game-start");
  if (screen == null) return Promise.resolve();
  return playGameStartExit(screen).catch((e) => {
    console.warn("[game-start-exit]", e.message);
  });
}

// Split from enterRun for the function-size budget: the phase branch (§ "Entering a table" steps
// 4-5).
async function dealOrResumeRun(state) {
  if (state.phase == null) {
    // The undealt table — mirrors boot.js#dealBoardBootRound's funnel exactly, so round 1 arrives
    // with the same deal choreography. A losing simultaneous two-member entry self-heals through
    // api()'s own 400 recovery (documented on dealBoardBootRound).
    armBiddingStageEntrance();
    await api("next-round", {});
    ensurePushStreamOpen(getCurrentGame());
  } else {
    // The in-flight round — the resumeLiveRound shape; renderFetchedState opens the push stream
    // itself.
    await renderFetchedState(state);
  }
}

/**
 * Enter an existing multiplayer table in-page — a run card's click, or, since PR 2, a your-turn
 * banner's — `startSession`'s sibling (multiplayer-entry-and-notifications design § "Entering a
 * table"). Every caller already sits on the start screen, so this owns playing the exit itself
 * (the same `exitDone`-then-act shape `startSession`'s caller builds for it) rather than taking it
 * as a parameter.
 *
 * `source` names the trigger for the `table-entered` analytics event (design § Analytics,
 * widened post-acceptance to `{source: card | banner | create, mode}`) — defaults to `"card"` so
 * the section's existing `enhanceTables(panel, {onEnter: enterRun})` call site (which invokes
 * this with exactly one argument, the gameId) needs no rewiring; `game-start-turn-banners.js`
 * passes `"banner"` explicitly, and `game-start-multiplayer-section.js`'s create-overlay
 * `onCreated` passes `"create"` — a freshly created table wasn't reached by clicking an existing
 * card, and `table-created` firing separately doesn't repair a mislabeled `table-entered` row in
 * the how-did-people-reach-a-table funnel.
 *
 * Session config is set from the parallel-fetched `session-info` before any render — skipping
 * that would render the table off the *previous* session's `hintsEnabled`/`autoPlay`/`gameMode`
 * (round-1 spec finding; `startSession`'s own config-before-render discipline,
 * boot.js#fetchSessionInfo's comment calls it universal).
 */
export async function enterRun(gameId, source = "card") {
  const exitDone = playStartScreenExit();
  setCurrentGame(gameId);
  try {
    const [sessionInfo, rawState] = await Promise.all([
      getGame("session-info"),
      getGame("state"),
    ]);
    setSessionConfig(sessionInfo);
    track("table-entered", {
      source,
      mode: sessionInfo.gameMode.toLowerCase(),
    });
    await exitDone;
    await dealOrResumeRun(decodeWireState(rawState));
  } catch (e) {
    // Never a half-torn screen — the same recovery startSession's failures use.
    handleStartSessionError("enter-run", e?.message ?? e);
  }
}

function buildSessionParams(settings) {
  const params = {
    hints: settings.hints,
    // Auto-play is a dev-only spectator toggle living in the dev overlay. Gating the read on
    // isDevMode() means leaving dev mode neutralizes it — no stuck spectator mode when the
    // overlay (its only off-switch) is hidden. localStorage holds strings; parse to boolean.
    autoPlay: isDevMode() && localStorage.getItem("autoPlay") === "true",
    gameMode: settings.gameMode,
    humanName: settings.playerName,
    // The active UI language, so the server-resolved default name ("Du"/"You")
    // matches the rendered UI even when the override differs from Accept-Language.
    lang: getActiveLanguage(),
  };
  // Dev/test affordance, not dev-gated (harmless if ever sent in prod): a numeric ?seed=
  // seeds the backend's Random, making the whole game (dealer seat + every deal) reproducible
  // — see game-flow-smoke.spec.js. Composes with ?dev=0 since the read doesn't consult
  // isDevMode() at all.
  const seedParam = new URLSearchParams(location.search).get("seed");
  if (
    seedParam != null &&
    seedParam !== "" &&
    Number.isInteger(Number(seedParam))
  ) {
    params.seed = Number(seedParam);
  }
  return params;
}

function handleStartSessionError(endpoint, status) {
  console.error(`${endpoint} failed: ${status}`);
  setHadApiError(true);
  setAnimating(false);
  fetchState();
}
